import React from 'react'
import { Draggable, Droppable } from '@hello-pangea/dnd'
import { Link } from 'react-router-dom'
import { useCatalogue } from '../CatalogueProvider'
import { NESTED_DROPPABLE_TYPE, nestedDroppableId } from '../catalogueOrdering'
import { CatalogueItem, CategoryNode } from '../catalogueTypes'
import { CatalogueEditActions, ExpandCategoryAction, CatalogueItemCells, CatalogueItemImage, CatalogueRowActions } from './CatalogueTableCells'
import { t } from '../../../i18n'

interface CatalogueItemRowProps {
  item: CatalogueItem
  index: number
  draggableId: string
  className: string
}

export const CatalogueItemRow = ({ item, index, draggableId, className }: CatalogueItemRowProps) => {
  const { state, meta } = useCatalogue()

  const handleClick = () => {
    if (item.class_name === 'Product') meta.go.show(item)
  }

  return (
    <Draggable draggableId={draggableId} index={index} isDragDisabled={!state.editMode}>
      {(provided, snapshot) => (
        <tr
          onClick={handleClick}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`${className} ${snapshot.isDragging ? 'dragging-row' : ''}`}
        >
          <CatalogueItemCells item={item} />
          <td className="row-actions-cell">
            <CatalogueRowActions>
              <CatalogueEditActions item={item} />
            </CatalogueRowActions>
          </td>
        </tr>
      )}
    </Draggable>
  )
}

const NestedCategoryRows = ({ category }: { category: CategoryNode }) => {
  const { state } = useCatalogue()

  return (
    <tr className="nested-table">
      <td colSpan={state.columns.length + 1} className="nested-table-cell">
        <div className="nested-subcategory">
          <table className="table table-robis">
            <Droppable droppableId={nestedDroppableId(category)} type={NESTED_DROPPABLE_TYPE}>
              {(provided) => (
                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                  {category.children.map((child, index) => (
                    <CatalogueItemRow
                      key={`${child.class_name}-${child.id}`}
                      item={child}
                      index={index}
                      draggableId={`nested-${child.class_name}-${child.id}`}
                      className="nested-row"
                    />
                  ))}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </table>
        </div>
      </td>
    </tr>
  )
}

const childrenText = (category: CategoryNode): string => t('catalogue.products_count', { count: category.children.length })

export const CatalogueCategoryRow = ({ category, index }: { category: CategoryNode; index: number }) => {
  const { state, actions, meta } = useCatalogue()
  const expanded = state.expandedTableCategoryIds.has(category.id)
  const toggle = () => actions.toggleTableCategory(category.id)

  return (
    <>
      <Draggable draggableId={`ProductCategory-${category.id}`} index={index} isDragDisabled={!state.editMode}>
        {(provided, snapshot) => (
          <tr
            onClick={toggle}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`${snapshot.isDragging ? 'dragging-row' : ''} gray-row`}
          >
            <td colSpan={state.columns.length}>
              <div>
                <CatalogueItemImage item={category} placeholder="categoryPlaceholder" className="table-row-image" />
                <Link to={meta.paths.category(category.id)} onClick={(event) => event.stopPropagation()}>
                  <span className="main-text">{category.name}</span>
                </Link>
                <span className="gray-text">{childrenText(category)}</span>
              </div>
            </td>
            <td className="row-actions-cell">
              <CatalogueRowActions>
                <CatalogueEditActions item={category} />
                <ExpandCategoryAction expanded={expanded} onToggle={toggle} />
              </CatalogueRowActions>
            </td>
          </tr>
        )}
      </Draggable>
      {expanded ? <NestedCategoryRows category={category} /> : null}
    </>
  )
}
